import { FormatSpecsAdminService } from '@inference.engine/assets-api-client/web';
import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import type {
  AuditEvent,
  FormatLibrary,
  FormatPatch,
  ImportPreview,
  NewFormatInput,
  NewGroupInput,
  SaveResult,
} from '../model/types';
import {
  auditEventPageSchema,
  formatLibrarySchema,
  importPreviewSchema,
  saveResultSchema,
} from './format-specs-admin.schemas';

/**
 * The refusals this page explains itself - a stale revision, an unconfirmed breaking change, an
 * invalid value - stay out of the global toast. Anything else still reaches it.
 */

const HANDLED_HERE = { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: '409,422' };

export async function getLibrary(signal?: AbortSignal): Promise<FormatLibrary> {
  const response = await FormatSpecsAdminService.library({
    client: assetsHttp,
    throwOnError: true,
    signal,
  });
  return formatLibrarySchema.parse(response.data);
}

export async function createFormat(body: NewFormatInput): Promise<SaveResult> {
  const response = await FormatSpecsAdminService.createFormat({
    client: assetsHttp,
    headers: HANDLED_HERE,
    throwOnError: true,
    body,
  });
  return saveResultSchema.parse(response.data);
}

export async function createGroup(body: NewGroupInput): Promise<SaveResult> {
  const response = await FormatSpecsAdminService.createGroup({
    client: assetsHttp,
    headers: HANDLED_HERE,
    throwOnError: true,
    body,
  });
  return saveResultSchema.parse(response.data);
}

export async function updateFormat(key: string, body: FormatPatch): Promise<SaveResult> {
  const response = await FormatSpecsAdminService.updateFormat({
    client: assetsHttp,
    headers: HANDLED_HERE,
    throwOnError: true,
    path: { key },
    body: body as never,
  });
  return saveResultSchema.parse(response.data);
}

export async function getFormatHistory(
  formatKey: string,
  signal?: AbortSignal,
): Promise<AuditEvent[]> {
  const response = await FormatSpecsAdminService.auditEvents({
    client: assetsHttp,
    throwOnError: true,
    signal,

    query: { entityType: 'format', entityKey: formatKey, pageSize: 50 },
  });
  return auditEventPageSchema.parse(response.data).data;
}

export async function restoreFormat(version: number, formatKey: string, confirmBreaking: boolean) {
  const response = await FormatSpecsAdminService.restore({
    client: assetsHttp,
    headers: HANDLED_HERE,
    throwOnError: true,
    path: { number: version },
    body: { formatKey, confirmBreaking },
  });
  return saveResultSchema.parse(response.data);
}

export async function exportLibrary(): Promise<Blob> {
  const response = await FormatSpecsAdminService.export({
    client: assetsHttp,
    throwOnError: true,

    path: { entity: 'all' },

    responseType: 'blob',
  });
  return response.data as Blob;
}

export async function createImport(files: File[], idempotencyKey: string): Promise<ImportPreview> {
  const response = await FormatSpecsAdminService.createImport({
    client: assetsHttp,
    headers: { ...HANDLED_HERE, 'Idempotency-Key': idempotencyKey },
    throwOnError: true,
    body: { files } as never,
  });
  return importPreviewSchema.parse(response.data);
}

export async function getImport(id: string): Promise<ImportPreview> {
  const response = await FormatSpecsAdminService.getImport({
    client: assetsHttp,
    throwOnError: true,
    path: { id },
  });
  return importPreviewSchema.parse(response.data);
}

export async function updateImport(
  id: string,
  body: { resolutions?: Record<string, 'file' | 'current'>; confirmBreaking?: boolean },
): Promise<ImportPreview> {
  const response = await FormatSpecsAdminService.updateImport({
    client: assetsHttp,
    headers: HANDLED_HERE,
    throwOnError: true,
    path: { id },
    body,
  });
  return importPreviewSchema.parse(response.data);
}

export async function confirmImport(id: string): Promise<SaveResult> {
  const response = await FormatSpecsAdminService.confirm({
    client: assetsHttp,
    headers: HANDLED_HERE,
    throwOnError: true,
    path: { id },
  });
  return saveResultSchema.parse(response.data);
}

export async function downloadIgnoredRows(id: string): Promise<Blob> {
  const response = await FormatSpecsAdminService.ignoredRows({
    client: assetsHttp,
    throwOnError: true,
    path: { id },

    responseType: 'blob',
  });
  return response.data as Blob;
}
